/**
 * Um bloco da tela "Contratos digitais" (E177.F4, GUS-253, ADR-0130; HTML 6.8 por CCB): "CCB i de
 * n", o número obrigatório antes do envio, a área de soltar do bloco e os arquivos dele.
 *
 * - O número é gravado ao sair do campo: declara a CCB (o bloco ainda sem número) ou corrige o número
 *   dela. O número de outro bloco é recusado na tela e no servidor.
 * - E177.F4.C01 (F-E177.F4-01): o número da CCB gravada que está apagado, repetido, com falha ao
 *   gravar, em gravação ou diferente do gravado deixa o bloco pendente (`onNumeroPendente`): o
 *   "Conferir" e a área de soltar esperam, porque o servidor só conhece o número gravado.
 * - A área de soltar só envia com a CCB gravada: cada lote abre o recebimento da CCB, e tudo o que
 *   entra nele tem nela a origem. E209.F3.T02 (GUS-296 item 12a): ela abre já com o número digitado;
 *   o que se solta antes da gravação aguarda no bloco (`useEsperaDoBloco`) e dispara a gravação.
 * - "Remover esta CCB" só no bloco sem arquivo, quando há mais de um.
 * - Do recibo, o envio do bloco sem conclusão (recarregar a página no meio do envio) segura o
 *   "Conferir" e se conclui pelo "Concluir o envio"; o guardado sem exame fica listado, e não conta
 *   como arquivo de origem (E133.F2.C01, E135.F2.C02).
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { AxiosInstance } from 'axios';

import { LinhaNaoExaminado, ReciboNaoSelado } from '@/components/CaseWizard/LinhasDoEnvio';
import { jaRecebido } from '@/components/ExameEletronico/pacote_do_banco';
import { FileDropzone } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { deleteDocument } from '@/lib/case_source_documents';
import {
  CcbError,
  corrigirCcb,
  declararCcb,
  desvincularDocumento,
  removerCcb,
} from '@/lib/ccbs_da_abertura';
import { jaRemovido, removerNaoExaminado } from '@/lib/recebimentos';
import { useEnvioPorRecebimento } from '@/lib/useEnvioPorRecebimento';
import { cn } from '@/lib/utils';

import {
  compartilhadaCom,
  nomeDoArquivo,
  normalizado,
  rotuloDaCcb,
  type BlocoDaCcb,
  type DoReciboDaCcb,
} from './blocosDasCcbs';
import { AbrirArquivo } from './AbrirArquivo';
import { useEsperaDoBloco } from './esperaDoBloco';
import {
  LINK_DA_CCB,
  LinhaEmEnvio,
  LinhaJaRecebida,
  LinhaRecebida,
  LinhaVinculada,
} from './LinhasDaCcb';

const t = ptBR.abertura.contratosDigitais;

export interface BlocoDaCcbProps {
  client: AxiosInstance;
  caseId: string;
  bloco: BlocoDaCcb;
  indice: number;
  blocos: readonly BlocoDaCcb[];
  onRecarregar: () => Promise<void>;
  /** O bloco sem número ganhou a CCB: a tela tira a chave local depois de reler e entrega ao bloco
   * da CCB os arquivos que esperavam o número (E209.F3.T02). */
  onDeclarada: (chaveLocal: string, ccbId: string, aEnviar: readonly File[]) => void;
  /** E209.F3.T02: os arquivos soltos antes da declaração, que este bloco (o da CCB) envia. */
  aEnviar?: readonly File[] | undefined;
  /** E209.F3.T02: os arquivos herdados já foram para o envio. */
  onEnviados?: (chave: string) => void;
  onTirarDaTela: (chaveLocal: string) => void;
  /** O que segura o Conferir no bloco: as pendências do envio e, delas, as com falha. */
  onPendencias: (chave: string, pendencias: number, falhas: number) => void;
  /** E177.F4.C01: o número da CCB gravada em edição (inválido, gravando ou não gravado). */
  onNumeroPendente: (chave: string, pendente: boolean) => void;
  /** Do recibo, os envios sem conclusão e os guardados sem exame da CCB. */
  doRecibo?: DoReciboDaCcb | undefined;
  /** E177.F4.T04: os vínculos do bloco (o painel do arquivo e o "Vincular documento já enviado"). */
  vinculos?: (documentoId: string) => ReactNode;
  depoisDosArquivos?: ReactNode;
}

type ErroDoNumero = 'vazio' | 'repetido' | 'erro' | null;

function useNumeroDaCcb(props: BlocoDaCcbProps, aEnviar: () => readonly File[]) {
  const { client, caseId, bloco, blocos } = props;
  const gravado = bloco.ccb?.numero ?? '';
  const [numero, setNumero] = useState(gravado);
  const [erro, setErro] = useState<ErroDoNumero>(null);
  const [gravando, setGravando] = useState(false);
  // A correção gravada e relida: o campo passa a mostrar o número como o servidor o guardou.
  useEffect(() => setNumero(gravado), [gravado]);
  const pendente = bloco.ccb !== null && (gravando || erro !== null || numero.trim() !== gravado);

  async function gravar(): Promise<void> {
    const valor = numero.trim();
    if (valor === gravado) {
      setErro(null);
      return;
    }
    if (valor === '') {
      // Apagar o número não apaga a CCB: o bloco fica pendente até ter um número.
      setErro('vazio');
      return;
    }
    const repetido = blocos.some(
      (b) =>
        b.chave !== bloco.chave &&
        b.ccb !== null &&
        normalizado(b.ccb.numero) === normalizado(valor),
    );
    if (repetido) {
      setErro('repetido');
      return;
    }
    setGravando(true);
    setErro(null);
    try {
      const declarada = bloco.ccb === null ? await declararCcb(client, caseId, valor) : null;
      if (bloco.ccb !== null) await corrigirCcb(client, caseId, bloco.ccb.id, valor);
      await props.onRecarregar();
      if (declarada !== null) props.onDeclarada(bloco.chave, declarada.id, aEnviar());
    } catch (err) {
      setErro(err instanceof CcbError && err.reason === 'repetida' ? 'repetido' : 'erro');
    } finally {
      setGravando(false);
    }
  }

  return { numero, setNumero, erro, gravando, gravar, pendente };
}

const MENSAGEM_DO_ERRO: Readonly<Record<Exclude<ErroDoNumero, null>, string>> = {
  vazio: t.numeroVazio,
  repetido: t.repetido,
  erro: t.erroAoGravar,
};

function CampoDoNumero({
  numero,
  setNumero,
  erro,
  gravando,
  gravar,
  testId,
}: ReturnType<typeof useNumeroDaCcb> & { testId: string }): JSX.Element {
  const id = useId();
  const mensagem = erro === null ? '' : MENSAGEM_DO_ERRO[erro];
  return (
    <div className="flex min-w-[220px] flex-1 flex-col gap-1">
      <label htmlFor={id} className="text-[13px] font-semibold text-fg">
        {t.numero} <span aria-hidden="true">*</span>
      </label>
      <input
        id={id}
        value={numero}
        onChange={(evt) => setNumero(evt.target.value)}
        onBlur={() => void gravar()}
        onKeyDown={(evt) => {
          if (evt.key === 'Enter') {
            evt.preventDefault();
            void gravar();
          }
        }}
        placeholder={t.numeroPlaceholder}
        autoComplete="off"
        disabled={gravando}
        aria-invalid={erro !== null}
        data-testid={testId}
        className="h-9 border border-border-strong bg-bg px-3 text-[14px] text-fg"
      />
      <small role="status" className="text-[13px] text-danger" data-testid={`${testId}-erro`}>
        {mensagem}
      </small>
    </div>
  );
}

/**
 * E199.F1 (POC, 31/10; GT-2026-0066): a remoção do guardado sem exame respondia 204 e a linha
 * seguia na tela; o clique seguinte dava 404 e "Não foi possível remover". Agora o que foi removido
 * (ou que o servidor já não tem) sai da tela na hora, a releitura que falha não vira erro de
 * remoção, e o botão fica desabilitado enquanto o pedido corre.
 */
function useRemocao(props: BlocoDaCcbProps) {
  const [removendo, setRemovendo] = useState<string | null>(null);
  const [removidos, setRemovidos] = useState<ReadonlySet<string>>(() => new Set());
  const [falhou, setFalhou] = useState(false);
  async function remover(acao: () => Promise<void>, chave: string): Promise<void> {
    setRemovendo(chave);
    setFalhou(false);
    try {
      await acao();
    } catch (err) {
      if (!jaRemovido(err)) {
        setFalhou(true);
        setRemovendo(null);
        return;
      }
    }
    setRemovidos((atual) => new Set([...atual, chave]));
    try {
      await props.onRecarregar();
    } catch {
      // A releitura que falha aparece pelo recibo não lido; a remoção já aconteceu.
    } finally {
      setRemovendo(null);
    }
  }
  return { removendo, removidos, falhou, remover };
}

type EnvioDoBloco = ReturnType<typeof useEnvioPorRecebimento>;
type Remocao = ReturnType<typeof useRemocao>;

/** As linhas do bloco: recibo não fechado, em envio, recebidas, vinculadas e guardadas sem exame. */
function ArquivosDoBloco({
  doBloco,
  envio,
  remocao,
}: {
  doBloco: BlocoDaCcbProps;
  envio: EnvioDoBloco;
  remocao: Remocao;
}): JSX.Element {
  const { client, caseId, bloco, blocos, doRecibo, vinculos } = doBloco;
  const vivo = (id: string): boolean => !remocao.removidos.has(id);
  const guardados = (doRecibo?.naoExaminados ?? []).filter((item) => vivo(item.id));
  const unidades = bloco.unidades.filter((u) => vivo(u.documento_id));
  const vinculadas = bloco.vinculadas.filter(({ unidade }) => vivo(unidade.documento_id));
  const usos = compartilhadaCom(blocos, bloco);
  const remover = (id: string, acao: () => Promise<void>): void => void remocao.remover(acao, id);
  const abrir = (documentoId: string, nome: string): JSX.Element => (
    <AbrirArquivo client={client} caseId={caseId} documentoId={documentoId} nome={nome} />
  );
  return (
    <div className="mt-2">
      {envio.recibosNaoSelados.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {envio.recibosNaoSelados.map((id) => (
            <ReciboNaoSelado
              key={id}
              recebimentoId={id}
              onConcluirDeNovo={() => void envio.concluirDeNovo(id)}
            />
          ))}
        </ul>
      ) : null}
      {envio.linhas.map((linha) =>
        linha.estado !== 'concluido' ? (
          <LinhaEmEnvio
            key={linha.localId}
            linha={linha}
            onTentarDeNovo={() => void envio.tentarDeNovo(linha.localId)}
            onRemover={() => envio.removerLinha(linha.localId)}
          />
        ) : linha.entrada !== undefined && jaRecebido(linha.entrada) ? (
          <LinhaJaRecebida key={linha.localId} linha={linha} />
        ) : null,
      )}
      {unidades.map((u) => (
        <LinhaRecebida
          key={u.documento_id}
          nome={nomeDoArquivo(blocos, u)}
          documentoId={u.documento_id}
          abrir={abrir(u.documento_id, nomeDoArquivo(blocos, u))}
          compartilhadaCom={(usos.get(u.documento_id) ?? []).map((c) =>
            t.rotulo.replace('{numero}', c.numero),
          )}
          removendo={remocao.removendo === u.documento_id}
          onRemover={() => remover(u.documento_id, () => deleteDocument(client, u.documento_id))}
          acoes={vinculos?.(u.documento_id)}
        />
      ))}
      {vinculadas.map(({ unidade, origem, nomeNaOrigem }) => (
        <LinhaVinculada
          key={unidade.documento_id}
          nome={nomeDoArquivo(blocos, unidade)}
          documentoId={unidade.documento_id}
          abrir={abrir(unidade.documento_id, nomeDoArquivo(blocos, unidade))}
          enviadaNa={origem !== null ? t.rotulo.replace('{numero}', origem.numero) : null}
          nomeNaOrigem={nomeNaOrigem}
          desvinculando={remocao.removendo === unidade.documento_id}
          onDesvincular={() =>
            remover(unidade.documento_id, () =>
              desvincularDocumento(client, caseId, bloco.chave, unidade.documento_id),
            )
          }
        />
      ))}
      {guardados.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-2">
          {guardados.map((item) => (
            <LinhaNaoExaminado
              key={item.id}
              item={item}
              removendo={remocao.removendo === item.id}
              onRemover={() => remover(item.id, () => removerNaoExaminado(client, caseId, item.id))}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** O bloco sem arquivo nenhum, nem em envio, nem guardado, nem com o recibo por fechar. */
function blocoVazio(bloco: BlocoDaCcb, envio: EnvioDoBloco, doRecibo?: DoReciboDaCcb): boolean {
  return (
    bloco.unidades.length === 0 &&
    bloco.vinculadas.length === 0 &&
    envio.linhas.every((l) => l.estado === 'concluido') &&
    (doRecibo?.naoExaminados.length ?? 0) === 0 &&
    envio.recibosNaoSelados.length === 0
  );
}

type NumeroDaCcb = ReturnType<typeof useNumeroDaCcb>;

/** E209.F3.T02: a área de soltar abre com o número digitado, sem erro e fora da gravação. */
function areaFechada(numero: NumeroDaCcb): boolean {
  return numero.numero.trim() === '' || numero.gravando || numero.erro !== null;
}

/** A linha sob a área: os arquivos à espera do número, o pedido do número ou o bloco vazio. */
function AvisoDoBloco(props: {
  esperando: number;
  semCcb: boolean;
  vazio: boolean;
  indice: number;
}): JSX.Element | null {
  if (props.esperando > 0) {
    return (
      <p
        role="status"
        className="mt-2 text-[13px] text-fg"
        data-testid={`ccb-espera-${props.indice}`}
      >
        {t.esperandoONumero.replace('{n}', String(props.esperando))}
      </p>
    );
  }
  if (props.semCcb) return <p className="mt-2 text-[13px] text-fg-muted">{t.informeONumero}</p>;
  return props.vazio ? <p className="mt-2 text-[13px] text-fg-muted">{t.nenhumArquivo}</p> : null;
}

export function BlocoDaCcbNaTela(props: BlocoDaCcbProps): JSX.Element {
  const { client, caseId, bloco, indice, blocos, onPendencias, onNumeroPendente } = props;
  const entregar = useRef<() => readonly File[]>(() => []);
  const numero = useNumeroDaCcb(props, () => entregar.current());
  const remocao = useRemocao(props);
  const envio = useEnvioPorRecebimento({
    client,
    caseId,
    kind: 'contested',
    ccbId: bloco.ccb?.id,
    abertosNoRecibo: props.doRecibo?.abertos,
    onEntrada: () => void props.onRecarregar(),
    onTalvezRecebido: () => void props.onRecarregar(),
    onConcluido: () => void props.onRecarregar(),
  });
  const { onEnviados } = props;
  const espera = useEsperaDoBloco({
    pronto: bloco.ccb !== null && !numero.pendente,
    herdados: props.aEnviar,
    enviar: envio.enviarLote,
    onEnviados: useCallback(() => onEnviados?.(bloco.chave), [onEnviados, bloco.chave]),
  });
  entregar.current = espera.entregar;
  const soltar = (arquivos: readonly File[]): void => {
    if (bloco.ccb !== null && !numero.pendente) {
      void envio.enviarLote(arquivos);
      return;
    }
    espera.guardar(arquivos);
    if (!numero.gravando) void numero.gravar();
  };
  const falhas =
    envio.linhas.filter((l) => l.estado === 'falhou').length + envio.recibosNaoSelados.length;
  useEffect(
    () => onPendencias(bloco.chave, envio.pendencias, falhas),
    [bloco.chave, envio.pendencias, falhas, onPendencias],
  );
  useEffect(
    () => onNumeroPendente(bloco.chave, numero.pendente),
    [bloco.chave, numero.pendente, onNumeroPendente],
  );
  const vazio = blocoVazio(bloco, envio, props.doRecibo);
  const titulo = t.bloco.replace('{i}', String(indice + 1)).replace('{n}', String(blocos.length));
  const tirar = (): void => {
    if (bloco.ccb === null) props.onTirarDaTela(bloco.chave);
    else void remocao.remover(() => removerCcb(client, caseId, bloco.chave), bloco.chave);
  };
  return (
    <fieldset
      className="my-5 border border-border-strong p-[18px]"
      data-testid={`ccb-bloco-${indice}`}
      aria-label={titulo}
    >
      <legend className="px-2 font-semibold text-fg">{titulo}</legend>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <CampoDoNumero {...numero} testId={`ccb-numero-${indice}`} />
        {blocos.length > 1 && vazio ? (
          <button
            type="button"
            className={LINK_DA_CCB}
            onClick={tirar}
            disabled={remocao.removendo !== null}
            data-testid={`ccb-remover-${indice}`}
          >
            {t.removerCcb}
          </button>
        ) : null}
      </div>
      <FileDropzone
        className={cn(
          'mt-3 border border-dashed border-fg-faint bg-bg p-[22px]',
          bloco.ccb === null && 'opacity-60',
        )}
        inputTestId={`ccb-arquivos-${indice}`}
        inputLabel={t.selecionar.replace('{ccb}', rotuloDaCcb(bloco, indice))}
        onFilesSelected={soltar}
        multiple
        disabled={areaFechada(numero)}
      >
        <span className="block font-sans text-[16px] font-semibold text-fg">{t.soltar}</span>
        <span className="mt-1.5 block text-[13px] text-fg-muted">{t.soltarAjuda}</span>
      </FileDropzone>
      <AvisoDoBloco
        esperando={espera.esperando}
        semCcb={bloco.ccb === null}
        vazio={vazio}
        indice={indice}
      />
      <ArquivosDoBloco doBloco={props} envio={envio} remocao={remocao} />
      {remocao.falhou ? (
        <p role="alert" className="mt-2 text-[13px] text-danger">
          {t.erroAoRemover}
        </p>
      ) : null}
      {props.depoisDosArquivos}
    </fieldset>
  );
}
